/**
 * SyncStatusPanel — diagnostic banner that explains the REAL state of
 * the open-banking sync for every connected account.
 *
 * Why this exists
 * ---------------
 * When an Italian SMB user connects PSD2 via A-Cube, the BE
 * provisioning + Temporal workflow chain look like this:
 *   1. ``provision_psd2_accounts`` → ``bank_accounts`` rows created
 *   2. ``_trigger_initial_sync(mode="full")`` → 24-month backfill
 *   3. ``client.list_transactions(...)`` → A-Cube call
 *   4. ``bulk_upsert_bank_transactions(...)`` → ``bank_transactions``
 *   5. ``mark_synced(...)`` → ``last_synced_at`` set REGARDLESS of count
 *
 * If the provider (A-Cube sandbox especially) returns ZERO transactions,
 * steps 1-2-3-5 succeed but step 4 is a no-op. The user sees a balance
 * (from step 1) but no movements — and without this panel, no clue WHY.
 *
 * The panel reads ``last_synced_at`` + ``sync_status`` per account and
 * answers the user's "perché non vedo niente?" question explicitly,
 * with a one-click "Sync ora" to re-fire the workflow.
 *
 * States rendered:
 *   - never-synced     → "Sincronizzazione mai eseguita"
 *   - in-flight        → "Sincronizzazione in corso…" (spinner)
 *   - synced-empty     → "Sync completata · 0 movimenti restituiti dal provider"
 *   - synced-with-data → "Aggiornato {time}" (subtle, success tone)
 *   - error            → "Errore · {sync_status}" + retry CTA
 */

import { CheckCircle2, Info, Loader2, RefreshCw } from "lucide-react";
import * as React from "react";
import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";

import { Button, cn } from "@ando/ui";

import type { BankAccountDTO } from "@ando/core";


/** Italian relative time — "2h fa" / "appena" / "il 4 giu". */
function formatLastSynced(iso: string | null, i18n: I18n): string {
  if (!iso) return i18n._(msg`mai`);
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "—";
  const diffMin = Math.round((Date.now() - t) / 60000);
  if (diffMin < 1) return i18n._(msg`appena ora`);
  if (diffMin < 60) return i18n._(msg`${diffMin}m fa`);
  const h = Math.round(diffMin / 60);
  if (h < 24) return i18n._(msg`${h}h fa`);
  const d = Math.round(h / 24);
  if (d < 30) return i18n._(msg`${d}g fa`);
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(t));
}


/** ISO → "<2 min" sentinel. */
function syncedJustNow(iso: string | null): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return Date.now() - t < 2 * 60_000;
}


export type SyncOutcome =
  | "never"
  | "in-flight"
  | "synced-empty"
  | "synced-with-data"
  | "error";


export interface SyncStatusPanelProps {
  readonly accounts: readonly BankAccountDTO[];
  readonly transactionsCount: number;
  readonly isSyncInFlight: boolean;
  readonly onSyncAll: () => void;
  readonly className?: string;
}


export const SyncStatusPanel: React.FC<SyncStatusPanelProps> = ({
  accounts,
  transactionsCount,
  isSyncInFlight,
  onSyncAll,
  className,
}) => {
  const { t, i18n } = useLingui();
  // Derive the panel-level outcome from the per-account signals.
  const outcome: SyncOutcome = React.useMemo(() => {
    if (isSyncInFlight) return "in-flight";
    if (accounts.length === 0) return "never";
    if (accounts.some((a) => a.sync_status === "error")) return "error";
    if (accounts.every((a) => a.last_synced_at === null)) return "never";
    if (transactionsCount === 0) return "synced-empty";
    return "synced-with-data";
  }, [accounts, transactionsCount, isSyncInFlight]);

  // Newest last_synced across accounts — what we show in the header.
  const newestSync = React.useMemo(() => {
    let max = 0;
    for (const a of accounts) {
      if (!a.last_synced_at) continue;
      const t = Date.parse(a.last_synced_at);
      if (Number.isFinite(t) && t > max) max = t;
    }
    return max > 0 ? new Date(max).toISOString() : null;
  }, [accounts]);

  const visual = OUTCOME_VISUAL[outcome];
  const Glyph = visual.icon;

  return (
    <section
      className={cn(
        // Pencil (Jul 2026): hairline-contour callout (HighlightNote pattern) —
        // no white fill, no drop-shadow; the semantic border carries the state.
        "mb-4 rounded-xl border bg-transparent p-4",
        visual.borderClass,
        className,
      )}
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={cn(
            "mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md",
            visual.iconWrapClass,
          )}
        >
          {outcome === "in-flight" ? (
            <Loader2
              size={14}
              strokeWidth={2}
              aria-hidden
              className="animate-spin motion-reduce:animate-none"
            />
          ) : (
            <Glyph size={14} strokeWidth={2} aria-hidden />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[12.5px] text-ink-1">
            {visual.title(
              i18n,
              outcome === "synced-with-data" ? transactionsCount : undefined,
            )}
          </div>
          <p className="mt-0.5 text-[11.5px] text-ink-2 leading-snug">
            {visual.description(i18n, newestSync, transactionsCount)}
          </p>
          {outcome === "synced-empty" ? (
            <p className="mt-1.5 text-[11px] text-ink-3 leading-snug">
              <Trans>
                Il provider (A-Cube) ha confermato la sincronizzazione ma non
                ha restituito alcun movimento per i conti collegati. Il
                <span className="font-medium"> sandbox di test </span>
                tipicamente espone solo metadati (saldo, IBAN) e non
                transazioni — passa a produzione o richiedi a A-Cube un VAT
                di test con dataset di mock-transazioni.
              </Trans>
            </p>
          ) : null}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onSyncAll}
          disabled={isSyncInFlight || accounts.length === 0}
          className="shrink-0 gap-1.5"
        >
          {isSyncInFlight ? (
            <Loader2
              size={12}
              strokeWidth={2}
              aria-hidden
              className="animate-spin motion-reduce:animate-none"
            />
          ) : (
            <RefreshCw size={12} strokeWidth={2} aria-hidden />
          )}
          {isSyncInFlight ? t`Sync…` : t`Sync ora`}
        </Button>
      </div>
      {/* Per-account sync stamps so the user verifies each conto
          individually when there's more than one. */}
      {accounts.length > 1 ? (
        <ul className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-[10.5px] text-ink-3 sm:grid-cols-2">
          {accounts.map((a) => (
            <li
              key={a.id}
              className="flex items-center justify-between gap-3 truncate"
            >
              <span className="truncate text-ink-2">
                {a.institution_name ?? a.account_name ?? t`Conto`}
              </span>
              <span
                className={cn(
                  "shrink-0 tabular-nums",
                  a.last_synced_at && syncedJustNow(a.last_synced_at)
                    ? "text-emerald-700"
                    : a.last_synced_at
                      ? "text-ink-3"
                      : "text-amber-700",
                )}
              >
                {a.last_synced_at
                  ? formatLastSynced(a.last_synced_at, i18n)
                  : t`mai sincronizzato`}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
};
SyncStatusPanel.displayName = "SyncStatusPanel";


// ── Outcome → visual + copy mapping ───────────────────────────────────


interface OutcomeVisual {
  readonly icon: typeof CheckCircle2;
  readonly iconWrapClass: string;
  readonly borderClass: string;
  readonly title: (i18n: I18n, count?: number) => string;
  readonly description: (
    i18n: I18n,
    newestSync: string | null,
    txCount: number,
  ) => string;
}


const OUTCOME_VISUAL: Readonly<Record<SyncOutcome, OutcomeVisual>> = {
  never: {
    icon: Info,
    iconWrapClass: "bg-amber-50 text-amber-700",
    borderClass: "border-amber-200/60",
    title: (i18n) => i18n._(msg`Sincronizzazione mai eseguita`),
    description: (i18n) =>
      i18n._(
        msg`Avvia la prima sincronizzazione per pullare i movimenti dal provider open-banking.`,
      ),
  },
  "in-flight": {
    icon: RefreshCw,
    iconWrapClass: "bg-ando-orange-soft text-ando-orange",
    borderClass: "border-ando-orange/20",
    title: (i18n) => i18n._(msg`Sincronizzazione in corso…`),
    description: (i18n) =>
      i18n._(
        msg`Il workflow Temporal sta pullando i movimenti dal provider. I dati compariranno entro pochi minuti.`,
      ),
  },
  "synced-empty": {
    icon: Info,
    iconWrapClass: "bg-amber-50 text-amber-700",
    borderClass: "border-amber-200/60",
    title: (i18n) => i18n._(msg`Sync completata · 0 movimenti restituiti`),
    description: (i18n, newestSync) => {
      const lastSync = formatLastSynced(newestSync, i18n);
      return i18n._(msg`Ultima sincronizzazione ${lastSync}.`);
    },
  },
  "synced-with-data": {
    icon: CheckCircle2,
    iconWrapClass: "bg-emerald-50 text-emerald-700",
    borderClass: "border-emerald-200/50",
    title: (i18n, count) =>
      count !== undefined
        ? i18n._(msg`Sincronizzato · ${count} movimenti caricati`)
        : i18n._(msg`Sincronizzato`),
    description: (i18n, newestSync) => {
      const lastSync = formatLastSynced(newestSync, i18n);
      return i18n._(
        msg`Aggiornato ${lastSync} · prossima sync automatica entro 4 ore.`,
      );
    },
  },
  error: {
    icon: Info,
    iconWrapClass: "bg-rose-50 text-rose-700",
    borderClass: "border-rose-200/60",
    title: (i18n) => i18n._(msg`Errore di sincronizzazione`),
    description: (i18n) =>
      i18n._(
        msg`Uno o più conti riportano errore di sync. Riprova ora oppure verifica il consenso PSD2.`,
      ),
  },
};
